// Personal feature toggles — every client can hide widgets/trackers they
// don't use so their dashboard stays focused on what matters. Backend keeps
// the source-of-truth map in `client_profiles.enabled_features`; this hook
// fetches it once per session and exposes a fast synchronous `isEnabled(key)`
// helper plus a `setFeature(key, bool)` mutator that PUTs back and updates
// local state optimistically.
//
// Defaults are ALL ON so existing users see zero change until they opt out.
import { useEffect, useState, useCallback, useSyncExternalStore } from "react";
import { api } from "@/lib/http";

export const FEATURE_KEYS = [
  "water",
  "weight",
  "sleep",
  "steps_wearable",
  "workouts",
  "supplements",
  "gamification",
  "pantry",
  "plate_solano",
  "mediterranean_score",
  "coach_ai",
];

const DEFAULTS = FEATURE_KEYS.reduce((a, k) => ((a[k] = true), a), {});

// --- Tiny module-level store so every mount shares the same map ------------
let _map = { ...DEFAULTS };
let _loaded = false;
let _inflight = null;
const _subs = new Set();

function _emit() {
  for (const cb of _subs) cb();
}

function _subscribe(cb) {
  _subs.add(cb);
  return () => _subs.delete(cb);
}

function _getSnapshot() {
  return _map;
}

async function _fetchOnce() {
  if (_loaded) return _map;
  if (_inflight) return _inflight;
  _inflight = api
    .get("/client/features")
    .then(({ data }) => {
      _map = { ...DEFAULTS, ...(data?.features || {}) };
      _loaded = true;
      _emit();
      return _map;
    })
    .catch(() => {
      // Silent fallback — if the endpoint fails (e.g. nutritionist calling
      // this hook by accident, or offline), we keep the all-ON defaults so
      // the app stays usable.
      _loaded = true;
      _emit();
      return _map;
    })
    .finally(() => {
      _inflight = null;
    });
  return _inflight;
}

export function useEnabledFeatures() {
  const map = useSyncExternalStore(_subscribe, _getSnapshot, _getSnapshot);
  const [loading, setLoading] = useState(!_loaded);

  useEffect(() => {
    let alive = true;
    if (!_loaded) {
      _fetchOnce().finally(() => alive && setLoading(false));
    } else {
      setLoading(false);
    }
    return () => {
      alive = false;
    };
  }, []);

  const isEnabled = useCallback(
    (key) => (key in map ? !!map[key] : true),
    [map]
  );

  const setFeature = useCallback(async (key, value) => {
    if (!FEATURE_KEYS.includes(key)) return;
    // Optimistic update
    const prev = _map;
    _map = { ..._map, [key]: !!value };
    _emit();
    try {
      const { data } = await api.put("/client/features", {
        features: { [key]: !!value },
      });
      _map = { ...DEFAULTS, ...(data?.features || {}) };
      _emit();
    } catch (e) {
      _map = prev;
      _emit();
      throw e;
    }
  }, []);

  return { features: map, isEnabled, setFeature, loading };
}
